import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Typography,
  Steps,
  Row,
  Col,
  Tag,
  Button,
  Space,
  Timeline,
  Divider,
  Progress,
  Input,
  Upload,
  message,
  Image,
  Spin,
} from 'antd';
import {
  TruckOutlined,
  MessageOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  UploadOutlined,
  QrcodeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { orderService, paymentService } from '../services/api';

const { Title, Paragraph, Text } = Typography;

const OrderTracking = () => {
  const { orderId } = useParams();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await orderService.trackOrder(orderId);
        setOrderData(response.data?.data || response.data);

        const qrResponse = await paymentService.getOrderQrCode(orderId);
        setQrCodeUrl(qrResponse.data?.data?.qr_code || null);

        fetchInvoice();
        fetchDeliveryLocation();
      } catch {
        if (!orderData) {
          setOrderData({
            id: orderId,
            status: 'pending',
            currentStep: 0,
            estimatedDelivery: '3-5 business days',
            progress: 0,
            refund_status: 'none',
            refund_status_display: 'No Refund',
            timeline: [
              {
                time: new Date().toLocaleString(),
                title: 'Order Placed',
                description: 'Your order has been placed',
                status: 'completed',
              },
              {
                title: 'Order Confirmed',
                description: 'Your order is being processed',
                status: 'waiting',
              },
              {
                title: 'Packaging',
                description: 'Your items are being packaged',
                status: 'waiting',
              },
              {
                title: 'Shipped',
                description: 'Your order is out for delivery',
                status: 'waiting',
              },
              {
                title: 'Delivered',
                description: 'Your order has been delivered',
                status: 'waiting',
              },
            ],
          });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const handleSubmitProof = async () => {
    if (!transactionId.trim() && !paymentProofFile) {
      message.warning('Please provide a transaction ID or upload a payment screenshot.');
      return;
    }
    try {
      setSubmitting(true);
      const formData = new FormData();
      if (transactionId.trim()) formData.append('transaction_id', transactionId.trim());
      if (paymentProofFile) formData.append('payment_proof_image', paymentProofFile);
      await paymentService.submitPaymentProof(orderId, formData);
      message.success('Payment proof submitted! Awaiting verification.');
      setTransactionId('');
      setPaymentProofFile(null);
      const response = await orderService.trackOrder(orderId);
      setOrderData(response.data?.data || response.data);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to submit payment proof. Please try again.';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const fetchInvoice = async () => {
    setInvoiceLoading(true);
    try {
      const response = await paymentService.getInvoice(orderId);
      setInvoiceData(response.data?.data || null);
    } catch {
      // No invoice available
    } finally {
      setInvoiceLoading(false);
    }
  };

  const fetchDeliveryLocation = async () => {
    setLocationLoading(true);
    try {
      const response = await orderService.getDeliveryLocation(orderId);
      setDeliveryLocation(response.data?.data || null);
    } catch {
      // No location available
    } finally {
      setLocationLoading(false);
    }
  };

  const downloadInvoice = async () => {
    if (!orderId) return;
    try {
      const response = await paymentService.downloadInvoice(orderId);
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-order-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.warning('Unable to download. Try View Invoice instead.');
      window.open(`/api/billing/customer-invoice/${orderId}/download`, '_blank');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Card>
          <TruckOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
          <Title level={4}>Loading tracking...</Title>
        </Card>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <Title level={4}>Order not found</Title>
        </Card>
      </div>
    );
  }

  const isQrPayment = orderData.payment_method === 'qr' && orderData.payment_status !== 'paid';

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Order Tracking</Title>
      <Paragraph style={{ color: '#666', marginBottom: '32px' }}>
        Track your order {orderId}
      </Paragraph>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card title="Order Status">
            <Steps current={orderData.currentStep || 0} direction="vertical" size="small">
              {(orderData.timeline || []).map((event, i) => (
                <Steps.Step
                  key={i}
                  title={event.title}
                  description={event.description}
                  status={
                    event.status === 'completed'
                      ? 'finish'
                      : event.status === 'processing'
                        ? 'process'
                        : 'wait'
                  }
                />
              ))}
            </Steps>
            <Divider />
            <Title level={5}>Delivery Progress</Title>
            <Progress
              percent={orderData.progress || 0}
              status="active"
              strokeColor="#1890ff"
              style={{ marginBottom: '16px' }}
            />
          </Card>
          <Card title="Detailed Timeline" style={{ marginTop: '24px' }}>
            <Timeline>
              {(orderData.timeline || []).map((event, i) => (
                <Timeline.Item
                  key={i}
                  color={
                    event.status === 'completed'
                      ? 'green'
                      : event.status === 'processing'
                        ? 'blue'
                        : 'gray'
                  }
                >
                  <Text strong>{event.title}</Text>
                  <br />
                  {event.time && <Text type="secondary">{event.time}</Text>}
                  <br />
                  <Text>{event.description}</Text>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>

          {isQrPayment && (
            <Card title="QR Payment" style={{ marginTop: '24px' }}>
              {qrCodeUrl ? (
                <div style={{ textAlign: 'center' }}>
                  <Image src={qrCodeUrl} alt="Payment QR Code" width={250} preview={false} />
                  <Paragraph style={{ marginTop: 16 }}>
                    <Text strong>Amount: NPR {orderData.total_amount || 'N/A'}</Text>
                  </Paragraph>
                  <Paragraph type="secondary">
                    Scan this QR code with your payment app to complete the payment, then submit the
                    proof below.
                  </Paragraph>
                </div>
              ) : (
                <Button
                  icon={<QrcodeOutlined />}
                  loading={qrLoading}
                  onClick={async () => {
                    setQrLoading(true);
                    try {
                      const res = await paymentService.getOrderQrCode(orderId);
                      setQrCodeUrl(res.data?.data?.qr_code);
                    } catch {
                      message.error('Failed to load QR code');
                    }
                    setQrLoading(false);
                  }}
                >
                  Show QR Code
                </Button>
              )}
              <Divider />
              <Title level={5}>Submit Payment Proof</Title>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Input
                  placeholder="Transaction ID"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                />
                <Upload
                  beforeUpload={(file) => {
                    setPaymentProofFile(file);
                    return false;
                  }}
                  onRemove={() => setPaymentProofFile(null)}
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>Upload Payment Screenshot</Button>
                </Upload>
                <Button
                  type="primary"
                  icon={<DollarOutlined />}
                  loading={submitting}
                  onClick={handleSubmitProof}
                >
                  Submit Payment Proof
                </Button>
              </Space>
            </Card>
          )}
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Order Details">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Order ID:</Text>
                <br />
                <Text>{orderData.id}</Text>
              </div>
              <div>
                <Text strong>Status:</Text>
                <br />
                <Tag
                  color={
                    orderData.status === 'refunded' || orderData.status === 'partially_refunded'
                      ? 'red'
                      : 'processing'
                  }
                >
                  {(orderData.status || 'PENDING').toUpperCase()}
                </Tag>
              </div>
              <div>
                <Text strong>Estimated Delivery:</Text>
                <br />
                <Text>{orderData.estimatedDelivery || 'N/A'}</Text>
              </div>
              {orderData.payment_method && (
                <div>
                  <Text strong>Payment:</Text>
                  <br />
                  <Text>{orderData.payment_method.toUpperCase()}</Text>
                </div>
              )}
              {orderData.payment_status && (
                <div>
                  <Text strong>Payment Status:</Text>
                  <br />
                  <Tag color={orderData.payment_status === 'paid' ? 'green' : 'orange'}>
                    {orderData.payment_status}
                  </Tag>
                </div>
              )}
              {orderData.refund_status && orderData.refund_status !== 'none' && (
                <div>
                  <Text strong>Refund Status:</Text>
                  <br />
                  <Tag
                    color={
                      orderData.refund_status === 'partial_refund_pending'
                        ? 'orange'
                        : orderData.refund_status === 'partially_refunded'
                          ? 'blue'
                          : orderData.refund_status === 'fully_refunded'
                            ? 'green'
                            : 'default'
                    }
                  >
                    {orderData.refund_status_display || orderData.refund_status}
                  </Tag>
                </div>
              )}
            </Space>
          </Card>
          <Card title="Invoice" style={{ marginTop: '24px' }}>
            {invoiceLoading ? (
              <div style={{ textAlign: 'center', padding: 12 }}>
                <Spin size="small" />
              </div>
            ) : invoiceData ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Invoice:</Text> {invoiceData.invoice_number}
                </div>
                <div>
                  <Text strong>Amount:</Text>{' '}
                  {Number(invoiceData.refunded_amount || 0) > 0 ? (
                    <>
                      <Text delete type="secondary">
                        NPR {Number(invoiceData.total_amount || 0).toLocaleString()}
                      </Text>
                      <Text strong style={{ color: '#ff4d4f', marginLeft: 8 }}>
                        NPR{' '}
                        {Number(
                          invoiceData.net_amount || invoiceData.total_amount || 0,
                        ).toLocaleString()}
                      </Text>
                    </>
                  ) : (
                    <Text strong>NPR {Number(invoiceData.total_amount || 0).toLocaleString()}</Text>
                  )}
                </div>
                {Number(invoiceData.refunded_amount || 0) > 0 && (
                  <Tag color="red">
                    Refunded: NPR {Number(invoiceData.refunded_amount).toLocaleString()}
                  </Tag>
                )}
                <Tag color={invoiceData.status === 'paid' ? 'green' : 'orange'}>
                  {invoiceData.status}
                </Tag>
                <Button type="primary" icon={<DownloadOutlined />} block onClick={downloadInvoice}>
                  Download Invoice
                </Button>
              </Space>
            ) : (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                block
                loading={invoiceLoading}
                onClick={downloadInvoice}
              >
                Get Invoice
              </Button>
            )}
          </Card>

          <Card title="Live Delivery" style={{ marginTop: '24px' }}>
            {locationLoading ? (
              <div style={{ textAlign: 'center', padding: 12 }}>
                <Spin size="small" />
              </div>
            ) : deliveryLocation && deliveryLocation.latitude ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ textAlign: 'center' }}>
                  <EnvironmentOutlined
                    style={{ fontSize: 28, color: '#1890ff', marginBottom: 8 }}
                  />
                  {deliveryLocation.delivery_person && (
                    <div>
                      <Text strong>{deliveryLocation.delivery_person.full_name}</Text>
                    </div>
                  )}
                </div>
                <Button
                  type="primary"
                  icon={<EnvironmentOutlined />}
                  block
                  href={`https://maps.google.com/?q=${deliveryLocation.latitude},${deliveryLocation.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Track on Google Maps
                </Button>
                <Button icon={<TruckOutlined />} block onClick={fetchDeliveryLocation}>
                  Refresh Location
                </Button>
              </Space>
            ) : (
              <div style={{ textAlign: 'center', padding: 12, color: '#9ca3af' }}>
                <TruckOutlined style={{ fontSize: 28, marginBottom: 8 }} />
                <div>Delivery location not available yet</div>
              </div>
            )}
          </Card>

          <Card style={{ marginTop: '24px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button type="primary" icon={<MessageOutlined />} block>
                Contact Support
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OrderTracking;
